import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';

const apiBase = process.env.NODE_ENV === 'production' 
  ? 'https://proofofprice.com' 
  : 'http://localhost:3001';
import { Theme } from '../theme';

interface AdminProps {
  theme: Theme;
}

interface LinkSubmission {
  id: number;
  link_url: string;
  user_email: string;
  user_name: string;
  submitted_via: string;
  status: 'pending' | 'processing' | 'processed' | 'failed';
  submission_date: string;
  processed_date?: string;
  extracted_data?: string;
  error_message?: string;
  notes?: string;
}

const AdminContainer = styled.div`
  background: ${props => props.theme.surface};
  color: ${props => props.theme.text};
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  grid-column: 1 / -1;
`;

const Title = styled.h2`
  color: ${props => props.theme.primary};
  margin-bottom: 20px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin-bottom: 20px;
  
  th, td {
    padding: 12px;
    border-bottom: 1px solid ${props => props.theme.border};
    text-align: left;
  }

  th {
    background: ${props => props.theme.tableHeaderBackground};
    font-weight: 600;
    position: sticky;
    top: 0;
    z-index: 10;
  }
  
  tr:hover {
    background: ${props => props.theme.hoverBackground};
  }
  
  a {
    color: ${props => props.theme.link};
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Button = styled.button`
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 8px;
  font-size: 14px;
  transition: all 0.2s;
`;

const ProcessButton = styled(Button)`
  background-color: ${props => props.theme.primary};
  border: 1px solid ${props => props.theme.primary};
  color: white;
  
  &:hover {
    opacity: 0.8;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.85em;
  background: ${props => {
    switch (props.status) {
      case 'processed': return props.theme.successBackground;
      case 'processing': return '#ffa500';
      case 'failed': return props.theme.failedBackground;
      default: return props.theme.pendingBackground;
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'processed': return props.theme.successText;
      case 'processing': return 'white';
      case 'failed': return props.theme.failedText;
      default: return props.theme.pendingText;
    }
  }};
  border: 1px solid currentColor;
`;

const LinkSubmissionsAdmin: React.FC<AdminProps> = ({ theme }) => {
  const [submissions, setSubmissions] = useState<LinkSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState<Set<number>>(new Set());

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      
      console.log('apiBase:', apiBase);
      console.log('Full URL:', `${apiBase}/api/link-submissions`);
      
      // Try using fetch instead of axios
      const response = await fetch(`${apiBase}/api/link-submissions`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (Array.isArray(data)) {
        setSubmissions(data);
        setError(null);
      } else {
        console.error('Unexpected response format:', data);
        setError('Unexpected response format from server');
      }
    } catch (err: any) {
      console.error('Error fetching link submissions:', err);
      setError(`Failed to fetch link submissions: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleProcess = async (id: number) => {
    try {
      setProcessing(prev => new Set(prev.add(id)));
      
      const response = await axios.post(`${apiBase}/api/link-submissions/${id}/process`);
      
      // Refresh the list
      await fetchSubmissions();
      
      // Show detailed success message from backend
      if (response.data?.message) {
        alert(`${response.data.message}\n\n📋 Check the "Pending" predictions tab to review and approve the extracted data.`);
      } else {
        alert('Link processed successfully! Check the Prediction Submissions tab for review.');
      }
    } catch (err: any) {
      console.error('Error processing link:', err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message;
      alert(`❌ Failed to process link: ${errorMessage}`);
    } finally {
      setProcessing(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  const truncateUrl = (url: string, maxLength: number = 50) => {
    if (url.length <= maxLength) return url;
    return url.substring(0, maxLength) + '...';
  };

  return (
    <AdminContainer theme={theme}>
      <Title theme={theme}>Link Submissions Admin</Title>
      
      {loading ? (
        <p>Loading link submissions...</p>
      ) : error ? (
        <p style={{ color: theme.failedText }}>{error}</p>
      ) : submissions.length === 0 ? (
        <p>No link submissions found</p>
      ) : (
        <Table theme={theme}>
          <thead>
            <tr>
              <th>ID</th>
              <th>Twitter Link</th>
              <th>Submitted</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {submissions.map(submission => (
              <tr key={submission.id}>
                <td>{submission.id}</td>
                <td>
                  <a href={submission.link_url} target="_blank" rel="noopener noreferrer">
                    {truncateUrl(submission.link_url)}
                  </a>
                  {submission.user_name && (
                    <div><small>By: {submission.user_name}</small></div>
                  )}
                </td>
                <td>{new Date(submission.submission_date).toLocaleString()}</td>
                <td>
                  <StatusBadge status={submission.status} theme={theme}>
                    {submission.status}
                  </StatusBadge>
                  {submission.error_message && (
                    <div><small style={{ color: theme.failedText }}>{submission.error_message}</small></div>
                  )}
                </td>
                <td>
                  {submission.status === 'pending' && (
                    <ProcessButton 
                      onClick={() => handleProcess(submission.id)} 
                      theme={theme}
                      disabled={processing.has(submission.id)}
                    >
                      {processing.has(submission.id) ? 'Processing...' : 'Process Tweet'}
                    </ProcessButton>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </AdminContainer>
  );
};

export default LinkSubmissionsAdmin;